// Styled employee combobox — replaces bare native <select>s where an
// employee is picked (first user: Inspection). Trigger renders as the
// page's own card; the popover is a searchable listbox with avatars,
// entity dot + code, full keyboard support (↑ ↓ Enter Escape) and
// click-outside close. Token-driven, so it follows light/dark.
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { ENTITIES, type EntityId } from '@pointage/shared';
import type { EmployeeDTO } from '../api/hooks.js';

const AV_COLORS = ['#7037ef', '#0bb5ab', '#ff5e11', '#d24df3', '#2a6fdb', '#c41d8f', '#0a9d72', '#d8590f'];
const avColor = (id: string) => AV_COLORS[[...id].reduce((a, c) => a + c.charCodeAt(0), 0) % AV_COLORS.length]!;
const initials = (name: string) =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
const entityOf = (id: string) => ENTITIES.find((e) => e.id === (id as EntityId));

export function EmployeePicker({ employees, value, onChange, testId }: {
  employees: EmployeeDTO[];
  value: string | null;
  onChange: (id: string) => void;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return employees;
    return employees.filter((e) =>
      `${e.name} ${e.code} ${e.department ?? ''}`.toLowerCase().includes(needle));
  }, [employees, q]);

  // Reset search + highlight the current value each time it opens.
  useEffect(() => {
    if (!open) return;
    setQ('');
    const idx = employees.findIndex((e) => e.id === value);
    setActive(idx >= 0 ? idx : 0);
    searchRef.current?.focus();
  }, [open, employees, value]);

  useEffect(() => { setActive(0); }, [q]);

  // Keep the active row in view while navigating.
  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (ev: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const pick = (id: string) => { onChange(id); setOpen(false); };

  const onKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.key === 'Escape') { setOpen(false); return; }
    if (ev.key === 'ArrowDown') { ev.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (ev.key === 'ArrowUp') { ev.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (ev.key === 'Enter') { ev.preventDefault(); const e = filtered[active]; if (e) pick(e.id); }
  };

  const current = employees.find((e) => e.id === value) ?? null;

  return (
    <div className="emp-picker-root" ref={rootRef} onKeyDown={onKeyDown}>
      <button
        type="button"
        className={'inspect-emp-picker emp-picker-trigger' + (open ? ' open' : '')}
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Employé"
      >
        <div className="inspect-emp-picker-avatar" style={current ? { background: avColor(current.id) } : undefined}>
          {current ? initials(current.name) : '··'}
        </div>
        <div className="inspect-emp-picker-info">
          <div className="inspect-emp-name">{current?.name ?? 'Choisir un employé…'}</div>
          {current && (
            <div className="inspect-emp-meta">
              {(() => {
                const ent = entityOf(current.entityId);
                return ent
                  ? <span className={`pill ${ent.short.toLowerCase()}`} style={{ padding: '1px 7px', fontSize: 10.5 }}>{ent.short}</span>
                  : null;
              })()}
              <span>{current.department ?? '—'}</span>
              <span style={{ color: 'var(--ink-4)' }}>·</span>
              <span style={{ fontFamily: 'var(--mono)' }}>{current.code}</span>
            </div>
          )}
        </div>
        <ChevronDown className={'emp-picker-chev' + (open ? ' up' : '')} size={17} aria-hidden="true" />
      </button>

      {open && (
        <div className="emp-pop" role="listbox" aria-label="Employés">
          <div className="emp-pop-search">
            <Search size={13} aria-hidden="true" />
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un employé…"
              data-testid={testId ? `${testId}-search` : undefined}
            />
            <span className="emp-pop-count">{filtered.length}</span>
          </div>
          <div className="emp-pop-list" ref={listRef}>
            {filtered.length === 0 && <div className="emp-pop-empty">Aucun employé ne correspond.</div>}
            {filtered.map((e, i) => {
              const ent = entityOf(e.entityId);
              return (
                <button
                  key={e.id}
                  type="button"
                  role="option"
                  aria-selected={e.id === value}
                  data-active={i === active}
                  className={'emp-pop-item' + (i === active ? ' active' : '') + (e.id === value ? ' selected' : '')}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(e.id)}
                >
                  <span className="emp-pop-av" style={{ background: avColor(e.id) }}>{initials(e.name)}</span>
                  <span className="emp-pop-main">
                    <span className="emp-pop-name">{e.name}</span>
                    <span className="emp-pop-sub">
                      {ent && <i className="emp-pop-dot" style={{ background: ent.color }} />}
                      {ent?.short ?? e.entityId}{e.department ? ` · ${e.department}` : ''} · {e.code}
                    </span>
                  </span>
                  {e.id === value && <span className="emp-pop-check" aria-hidden="true">✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
